"use client";

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { dateFormat, formatPriceRange, formatPrice } from "@/app/utilities/helper";
import { getVariantList, getAllUserReviews } from '@/app/utilities/apis';
import LeadButton from "../ui/Lead/LeadButton";
import VariantSelect from './VariantSelect';

import { convertModelTags, findSpecValue } from '@/app/utilities/modelTagConverter';
import EmiStartCard from './EmiStartCard';

import { useEmiData } from "@/hooks/useEmiData";
import { useRTOPricingData } from "@/hooks/useRTOPricingData";
import { PDP_EMICALCULATOR_POPUP_TRACKING } from '@/lib/constants/tracking';
import Image from 'next/image';
import Link from "next/link";
import BodyContent from "../ui/BodyContent/BodyContent";
import dynamic from 'next/dynamic';
import { getOnRoadPrice } from '@/utils/onRoadPrice';
import {
    CITY_PRICE_INSURANCE_TOOLTIP_TEXT,
    CITY_PRICE_TOTAL_ON_ROAD_TOOLTIP_TEXT,
    formatRupeeFull,
} from './revamp-components/cityPriceDescription';
import CityPriceDescriptionBlock from './revamp-components/CityPriceDescriptionBlock';

const EmiCalculator = dynamic(() => import("../ui/EmiCalculator/EmiCalculator"));

const CDN_STATIC = process.env.NEXT_PUBLIC_CDN_STATIC_URL ?? "";

/** Desktop hover + mobile tap; same pattern as Pricebreakup InfoTooltip */
function PriceSummaryInfoTooltip({ text, label }: { text: string; label: string }) {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        if (!open) return;
        function handleOutsideClick(e: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        }
        document.addEventListener("click", handleOutsideClick);
        return () => document.removeEventListener("click", handleOutsideClick);
    }, [open]);

    return (
        <span
            ref={wrapperRef}
            className="group relative ml-1.5 inline-flex items-center"
            onClick={(e) => {
                e.stopPropagation();
                setOpen((prev) => !prev);
            }}
        >
            <Image
                src={`${CDN_STATIC}search-icons/exclamatory-white.svg`}
                alt=""
                aria-hidden
                width={14}
                height={14}
                className="cursor-pointer rounded-full border border-[#545454]"
            />
            <span className="sr-only">{label}</span>
            <span
                role="tooltip"
                className={`${open ? "visible" : "invisible"} md:invisible md:group-hover:visible absolute z-[9999] w-64 p-3 ml-2 text-left text-xs font-normal text-black bg-white border border-gray-200 rounded-lg shadow-xl left-full top-1/2 -translate-y-1/2`}
            >
                {text}
                <span className="absolute -left-1 top-1/2 h-2 w-2 -translate-y-1/2 rotate-45 border-l border-t border-gray-200 bg-white transform" />
            </span>
        </span>
    );
}

const OFFER_MONTH = new Date().toLocaleString('en-IN', { month: 'long' });

const getVariantDisplayName = (modelName: string, variantItem: any, fallback = 'Base') => {
    const name = variantItem?.name?.trim();
    if (!name) return fallback;
    if (modelName && name.toLowerCase().includes(modelName.toLowerCase())) return name;
    return `${modelName} ${name}`.trim();
};

const extractCcAndFuel = (variantItem: any) => {
    const rawFuel = variantItem?.fuel_types || findSpecValue(variantItem, 'fuel_types') || '';
    const fuel = Array.isArray(rawFuel) ? rawFuel[0] || '' : String(rawFuel || '');
    const cc =
        variantItem?.engine ||
        variantItem?.engine_displacement ||
        findSpecValue(variantItem, 'engine_displacement') ||
        '';
    return { cc: cc ? String(cc) : '', fuel };
};

interface Props {
    data: any;
    gorpTrackingClass?: string;
    isPriceInCityRoute?: boolean;
    isVariantRoute?: boolean;
    variantSlug?: string;
    GORPFormHeading?: React.ReactNode;
    h1Title?: string;
    titleTag?: "h1" | "h2";
    /** Top intro copy shown under the title (spec / mileage / range desktop hero) */
    heroDescription?: string;
    useSplashPopup?: boolean;
    // Prefetched values from server (optional)
    prefetchedReviewRating?: number;
    prefetchedReviewCount?: number;
    prefetchedRtoResult?: any;
    prefetchedInsuranceResult?: any;
    prefetchedVariantId?: string | number;
    prefetchedCity?: string;
    prefetchedCitySlug?: string;
    leadCTAText?: string;
    is_discontinued?: boolean;
    EmiStartCardTracking?: string;
    isPDPRoute?: boolean;
    prefetchedVariantRes?: any;
    enableOnRoadPricing?: boolean;
    // Pages like Specs/Mileage should always reflect the model's base variant
    // rather than whatever variant the user last picked elsewhere on the site.
    lockToBaseVariant?: boolean;
    /** Desktop price page: title + city intro render in the top section instead */
    hideCityPriceTitleAndIntro?: boolean;
}

const getPrefetchedVariants = (prefetchedVariantRes: any) => {
    if (Array.isArray(prefetchedVariantRes)) return prefetchedVariantRes;
    if (Array.isArray(prefetchedVariantRes?.data)) return prefetchedVariantRes.data;
    return [];
};

const ProductHeroSection: React.FC<Props> = ({ data, gorpTrackingClass, isPriceInCityRoute = false, prefetchedReviewRating, prefetchedReviewCount, prefetchedRtoResult, prefetchedInsuranceResult, prefetchedVariantId, prefetchedCity, prefetchedCitySlug, isVariantRoute = false, variantSlug = '', GORPFormHeading, h1Title, titleTag = "h1", heroDescription, useSplashPopup = false, leadCTAText, is_discontinued, EmiStartCardTracking, isPDPRoute = false, prefetchedVariantRes, enableOnRoadPricing = true, lockToBaseVariant = false, hideCityPriceTitleAndIntro = false }) => {
    const model = data?.modelData || data;
    const variant = Array.isArray(model?.base_variant) ? model?.base_variant[0] : model?.base_variant;
    const prefetchedVariants = useMemo(() => getPrefetchedVariants(prefetchedVariantRes), [prefetchedVariantRes]);

    const [variants, setVariants] = useState<any[]>(prefetchedVariants.length > 0 ? prefetchedVariants : Array.isArray(model?.base_variant) ? model.base_variant : model?.base_variant ? [model.base_variant] : []);
    const [selectedVariant, setSelectedVariant] = useState<any>(variant);
    const [showVariantDropdown, setShowVariantDropdown] = useState(false);

    const [reviewRating, setReviewRating] = useState<number>(Number(prefetchedReviewRating ?? model?.user_rating ?? 0));
    const [reviewCount, setReviewCount] = useState<number>(Number(prefetchedReviewCount ?? model?.reviews_count ?? 0));
    const [isFetchingReviews, setIsFetchingReviews] = useState<boolean>(false);

    const router = useRouter();
    const isDiscontinued = is_discontinued || model?.status === 'discontinued';
    const variantDropdownRef = useRef<HTMLDivElement>(null);
    const cookieAppliedRef = useRef(false);

    const saveSelVariantCookie = (modelSlug: string, variantSlug: string) => {
        if (!modelSlug || !variantSlug) return;
        document.cookie = `_selVariant=${encodeURIComponent(`${modelSlug}:${variantSlug}`)};path=/;max-age=${60 * 60 * 24 * 30}`;
    };
    const price = model?.price_range?.base || selectedVariant?.price || variant?.price || 0;
    const selectedInfo = extractCcAndFuel(selectedVariant);

    const min_price_variant = Array.isArray(model?.variants) ? model.variants.reduce((min: any, v: any) => {
        const vPrice = Number(v.price ?? 0);
        if (vPrice < (min.price ?? Infinity)) {
            return v;
        }
        return min;
    }, model.variants[0]) : null;

    let min_onRoad_price = 0;

    if (min_price_variant && model?.locationInfo && isPriceInCityRoute && enableOnRoadPricing) {
        const minPriceVariantOnRoad = getOnRoadPrice(min_price_variant, model?.locationInfo);
        if (minPriceVariantOnRoad) {
            min_onRoad_price = minPriceVariantOnRoad.onRoadPrice || 0;
        }
    }

    // custom hook to get rto and insurance pricing data for selected variant and city
    const { rtoResult, insuranceResult, isRtoLoading, isInsuranceLoading, selectedCity } = useRTOPricingData({
        model,
        selectedVariant,
        price,
        isPriceInCityRoute,
        prefetchedRtoResult,
        prefetchedInsuranceResult,
        prefetchedVariantId,
        prefetchedCity,
        extraDeps: [selectedInfo.cc],
        syncCityFromCookieOnMount: true,
    });
    const isUpcoming = model?.status === 'upcoming' || Boolean(model?.is_upcoming);
    const rating = reviewRating;
    const reviews = reviewCount;
    const displayPrice = formatPriceRange(model?.price_range, true);
    const hasDisplayPrice = Boolean(model?.price_range?.base || model?.price_range?.top || selectedVariant?.price || variant?.price);
    const baseTitle = h1Title ? h1Title : model?.h1_title
        ? convertModelTags(model.h1_title, model)
        : `${model?.brand_name || ''} ${model?.name || ''}${isPriceInCityRoute ? (model?.locationInfo?.name ? ` Price in ${model?.locationInfo?.name}` : ' price') : ''}`;
    const title = isPriceInCityRoute && min_onRoad_price > 0 
        ? `${baseTitle} - Starts at ${formatPrice(min_onRoad_price)}`
        : baseTitle;

    // Prefetch variants if flag is set and variants are not already provided by server
    useEffect(() => {
        let mounted = true;
        const fetchVariantsList = async () => {
            try {
                if (!model?._id) return;
                if (prefetchedVariants.length > 0) return;
                const res: any = await getVariantList(model._id, 1, 1000);
                const list = res?.data || [];
                if (!mounted) return;

                if (list.length > 0) {
                    setVariants(list);
                    if (!selectedVariant?.name) setSelectedVariant(list[0]);
                }
            } catch (e) {
                // silent
            }
        };
        fetchVariantsList();
        return () => { mounted = false; };
    }, [model?._id, prefetchedVariants.length]);

    // Fetch reviews on client since they can be dynamic and frequently updated, and also to get the most recent reviews count for accurate display
    useEffect(() => {
        let mounted = true;
        const fetchReviews = async () => {
            // If server already provided reviews, skip client fetch
            if (prefetchedReviewRating !== undefined && prefetchedReviewCount !== undefined) return;
            try {
                setIsFetchingReviews(true);
                if (!model?._id) return;
                const res = await getAllUserReviews({ model_id: model._id, limit: 50, page: 1 });
                if (!mounted) return;

                if (Array.isArray(res?.reviews) && res.reviews.length > 0) {
                    const numericRatings = res.reviews
                        .map((item: any) => Number(item?.rating))
                        .filter((value: number) => !Number.isNaN(value) && value > 0);

                    const avgRating = numericRatings.length > 0
                        ? numericRatings.reduce((sum: number, value: number) => sum + value, 0) / numericRatings.length
                        : 0;

                    setReviewRating(avgRating);
                    setReviewCount(Number(res?.pagination?.total ?? res.reviews.length));
                    return;
                }

                setReviewRating(Number(model?.user_rating ?? 0));
                setReviewCount(Number(model?.reviews_count ?? 0));
            } catch (e) {
                if (!mounted) return;
                setReviewRating(Number(model?.user_rating ?? 0));
                setReviewCount(Number(model?.reviews_count ?? 0));
            } finally {
                setIsFetchingReviews(false);
            }
        };

        fetchReviews();
        return () => { mounted = false; };
    }, [model?._id, model?.user_rating, model?.reviews_count]);

    // On mount: read _selVariant cookie and pre-select the matching variant.
    // Skip for isPriceInCityRoute — server already initialised base_variant from the cookie.
    // Skip for lockToBaseVariant — pages like Specs/Mileage must always show the base variant's EMI.
    useEffect(() => {
        if (isPriceInCityRoute || lockToBaseVariant || cookieAppliedRef.current || variants.length === 0) return;
        const match = document.cookie.match(/(?:^|;\s*)_selVariant=([^;]*)/);
        if (!match) return;
        const decoded = decodeURIComponent(match[1]);
        const colonIdx = decoded.indexOf(':');
        if (colonIdx === -1) return;
        const cookieModel = decoded.slice(0, colonIdx);
        const cookieVariant = decoded.slice(colonIdx + 1);
        if (cookieModel !== model?.slug || !cookieVariant) return;
        const found = variants.find((v: any) => v.slug === cookieVariant);
        if (found) {
            setSelectedVariant(found);
            cookieAppliedRef.current = true;
        }
    }, [variants, model?.slug, isPriceInCityRoute, lockToBaseVariant]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (variantDropdownRef.current && !variantDropdownRef.current.contains(event.target as Node)) {
                setShowVariantDropdown(false);
            }
        };

        if (showVariantDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showVariantDropdown]);

    const handleOpenLocation = () => {
        window.dispatchEvent(new CustomEvent('open-location-modal'));
    };

    // custom hook to get EMI data for selected variant and city
    const { emiData, isEmiOpen, emiSelectedVariant, emiOnRoadPrices, handleOpenEmiCalculator, setIsEmiOpen, setEmiSelectedVariant } = useEmiData({
        model,
        selectedVariant,
        variants,
        selectedCity,
        isPriceInCityRoute,
        rtoResult,
        insuranceResult,
        isRtoLoading,
        isInsuranceLoading,
        price,
    });

    // find current variant details
    const currentVariant = variants?.find((v: any) => v?.slug?.trim() === variantSlug?.trim());
    
    // variants options
    const brandSlug = data?.brand_slug || data?.brand?.slug || '';
    const currentVariantOption = {
        label: `${model?.name || ''} ${currentVariant?.name || ''}`.trim(),
        slug: currentVariant?.slug,
        url: `/${brandSlug}/${data?.slug}/${currentVariant?.slug}`,
        price: currentVariant?.price
    }
    const otherVariants = variants?.filter((v: any) => v?.slug?.trim() !== variantSlug?.trim() && v.status !== 'upcoming' && v.is_sanity_done)
        .map((v: any) => ({
            label: `${model?.name || ''} ${v.name || ''}`.trim(),
            slug: v.slug,
            url: `/${brandSlug}/${data?.slug}/${v.slug}`,
            price: v?.price
        }));

    const variantsOptions = [currentVariantOption, ...otherVariants];
    const displayCity = selectedCity || prefetchedCity || model?.locationInfo?.name || '';
    const modelName = model?.name || '';
    const exShowroomCity = Number(selectedVariant?.price || price) || 0;
    const rtoTotalCity = Number(rtoResult?.total) || 0;
    const insuranceTotalCity = Number(insuranceResult?.total) || 0;
    const totalOnRoadCity = exShowroomCity + rtoTotalCity + insuranceTotalCity;
    const cityPriceRows = isUpcoming
        ? [
              {
                  label: "Estimated Ex-Showroom Price",
                  value: exShowroomCity
                      ? formatRupeeFull(exShowroomCity)
                      : isUpcoming
                        ? "Price to be Announced"
                        : "Price on request",
              },
              {
                  label: "Estimated Other Charges",
                  value:
                      rtoResult || insuranceResult
                          ? formatRupeeFull(rtoTotalCity + insuranceTotalCity)
                          : "",
              },
          ]
        : [
              {
                  label: "Ex-Showroom Price",
                  value: exShowroomCity ? formatRupeeFull(exShowroomCity) : "Price on request",
              },
              {
                  label: "RTO, Registration",
                  value: rtoResult ? formatRupeeFull(rtoTotalCity) : "",
              },
              {
                  label: "Insurance",
                  value: insuranceResult ? formatRupeeFull(insuranceTotalCity) : "",
                  tooltip: {
                      label: "Insurance premium details",
                      text: CITY_PRICE_INSURANCE_TOOLTIP_TEXT,
                  },
              },
          ];

    return (
        <>
            <div data-testid="desktop-hero-section">
                <div className="bg-white p-4 flex flex-col justify-start sticky top-24 relative rounded-lg h-fit">

                    {!(isPriceInCityRoute && hideCityPriceTitleAndIntro) &&
                        (titleTag === "h2" ? (
                            <h2 className="font-medium text-[20px] leading-[28px] text-gray-900 mb-3 w-full max-w-full md:max-w-[80%]">
                                {title}
                            </h2>
                        ) : (
                            <h1 className="font-medium text-[20px] leading-[28px] text-gray-900 mb-3 w-full max-w-full md:max-w-[80%]">
                                {title}
                            </h1>
                        ))}

                    {!isPriceInCityRoute && (reviews > 0 || isFetchingReviews) && (
                        <div className="hero-review-rating mb-4">
                            <span className="inline-flex items-center gap-2 px-8 py-1 bg-[#EFF6FF] rounded-[10px] text-[14px] text-gray-700">
                                <Image
                                    src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}yellow-star.svg`}
                                    alt="rating"
                                    width={12}
                                    height={12}
                                    className="w-[12px] h-[12px] filter invert"
                                    title="rating"
                                />
                                <span className="text-[#1447E6]">{isFetchingReviews ? '—' : rating.toFixed(1)}</span>
                                {/* <span className="text-[#4A5565]">User ({reviews})</span> */}
                            </span>
                        </div>
                    )}

                    {isPriceInCityRoute && !hideCityPriceTitleAndIntro ? (
                        <CityPriceDescriptionBlock model={model} variants={variants} cityName={displayCity} />
                    ) : !isPriceInCityRoute ? (
                        heroDescription && (
                            <div className="mb-4">
                                <BodyContent
                                    fullText={heroDescription}
                                    containerClassName=""
                                    className="line-clamp-3"
                                />
                            </div>
                        )
                    ) : null}

                    {isVariantRoute && (
                        <div className="mb-3">
                            <VariantSelect options={variantsOptions} defaultSelected={currentVariantOption} modelSlug={model?.slug} />
                        </div>
                    )}


                    {!isPriceInCityRoute && (
                        <div className={`mb-6 ${!isVariantRoute ? '' : 'bg-[#008C8B0D] border-[0.4px] border-[#008C8B26] rounded-lg p-4'}`}>
                            {hasDisplayPrice ? (
                                <>
                                    <p className="text-sm text-gray-500">
                                        {isUpcoming ? "Estimated Price" : "Ex-Showroom Price"}
                                    </p>
                                    <span className="soab text-gray-900" dangerouslySetInnerHTML={{ __html: displayPrice }}></span>

                                    {isPDPRoute && (
                                        <Link prefetch={false} href={`/${brandSlug}/${data?.slug}/price-in-${prefetchedCitySlug || ''}`} className="text-[14px] text-[#155DFC] leading-[20px] font-semibold ml-4 md:ml-2">
                                            Get On Road Price
                                        </Link>
                                    )}

                                    {isUpcoming && (
                                        <p className="text-sm text-gray-700 mt-2">
                                            Expected Date of Launch :{" "}
                                            <b>{model?.launch_date ? dateFormat(model?.launch_date) : "Coming Soon"}</b>
                                        </p>
                                    )}
                                </>
                            ) : (
                                <div className="bg-[#008C8B0D] rounded-xl p-3">
                                    <p className="font-roboto font-bold text-[20px] leading-[28.8px] text-[#008C8B] mb-1">
                                        {"Price to be Announced"}
                                    </p>
                                    <p className="text-sm text-gray-700">
                                        Expected Date of Launch :{" "}
                                        <b>{model?.launch_date ? dateFormat(model?.launch_date) : "Coming Soon"}</b>
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {isPriceInCityRoute && (hasDisplayPrice || isUpcoming) && (
                        <>
                            <h2 className="mb-4 text-[20px] font-bold leading-[26px] text-[#101828]">
                                {`${model?.brand_name || ''} ${modelName} On Road Price in ${displayCity}`.trim()}
                            </h2>
                            <div className="mb-4 flex gap-3">
                                {variants.length > 0 &&
                                    <div ref={variantDropdownRef} className="relative flex-1">
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => setShowVariantDropdown(s => !s)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') setShowVariantDropdown(s => !s); }}
                                            className="rounded-lg border border-gray-200 px-4 py-2 cursor-pointer"
                                        >
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-[12px] text-[#6A7282]">Variant</p>
                                                    <div className="flex items-center gap-2">
                                                        <p className="text-[12px] text-[#101828]">
                                                            {selectedVariant?.name
                                                                ? getVariantDisplayName(modelName, selectedVariant)
                                                                : 'Select'}
                                                        </p>
                                                        {selectedVariant?.is_top_variant && (
                                                            <span className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded">Top</span>
                                                        )}
                                                        {selectedVariant?.is_base_variant && (
                                                            <span className="text-[10px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded">Base</span>
                                                        )}
                                                    </div>
                                                </div>

                                                <span className="text-gray-400 text-xl">›</span>
                                            </div>
                                        </div>

                                        {showVariantDropdown && (
                                            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-2xl border border-gray-200 max-h-[320px] overflow-auto z-50">
                                                <div className="space-y-1 p-2">
                                                    {variants.filter((v: any) => v?.status !== 'discontinued').map((v: any, idx: number) => {
                                                        const info = extractCcAndFuel(v);
                                                        const isSelected = selectedVariant && (selectedVariant._id === v._id || selectedVariant.name === v.name);
                                                        return (
                                                            <div
                                                                key={v._id || v.name || idx}
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={(e) => { e.stopPropagation(); setSelectedVariant(v); setShowVariantDropdown(false); saveSelVariantCookie(model?.slug, v?.slug); router.refresh(); }}
                                                                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setSelectedVariant(v); setShowVariantDropdown(false); saveSelVariantCookie(model?.slug, v?.slug); router.refresh(); } }}
                                                                className={`flex items-start justify-between gap-3 px-3 py-2 cursor-pointer transition-colors border-b border-gray-200 ${isSelected ? 'bg-blue-50 border border-blue-100' : 'hover:bg-gray-50'}`}
                                                            >
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center gap-2">
                                                                        <div className="text-sm font-medium text-gray-900 truncate">
                                                                            {getVariantDisplayName(modelName, v)}
                                                                        </div>
                                                                        {v?.is_top_variant && (
                                                                            <span className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded flex-shrink-0">Top</span>
                                                                        )}
                                                                        {v?.is_base_variant && (
                                                                            <span className="text-[10px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded flex-shrink-0">Base</span>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex items-center justify-between mt-1">
                                                                        <div className="text-[12px] text-gray-500">
                                                                            {info.cc}{info.cc && info.fuel ? ' • ' : ''}{info.fuel}
                                                                        </div>
                                                                        <div className="text-[12px] text-gray-500 text-right">{v.price ? formatPrice(Number(v.price)) : ''}</div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                }

                                {/* City Card - only show on price-in-city route */}
                                <div role="button"
                                    tabIndex={0}
                                    onClick={handleOpenLocation}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleOpenLocation(); }}
                                    className="flex-1 rounded-lg border border-[#0A86F126] px-4 py-2 bg-[#0A86F10D] cursor-pointer">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-[12px] text-[#6A7282]">City</p>
                                            <p
                                                className="text-[12px] text-[#155DFC] font-semibold flex items-center gap-2 cursor-pointer">
                                                {/* const fallbackCity = `${model?.locationInfo?.name}, ${model?.locationInfo?.state?.name}`; */}
                                                {/* const fallbackCity = `${model?.locationInfo?.name}`; */}
                                                {selectedCity || `${model?.locationInfo?.name}`}
                                                <Image src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}pencil.svg`} alt="Edit location" title="Edit location" width={12} height={12} className="w-3 h-3" />
                                            </p>
                                        </div>
                                        <span className="text-gray-400 text-xl">›</span>
                                    </div>
                                </div>

                            </div>

                            <div id="price" className="mb-3 overflow-visible">
                                <p className="mb-2 text-[13px] font-bold leading-[17px] text-[#101828]">Price Summary</p>
                                <table className="w-full border-collapse overflow-visible">
                                    <tbody>
                                        {cityPriceRows.map((row) => (
                                            <tr key={row.label}>
                                                <td className="overflow-visible pb-2  text-left text-[14px] leading-5 font-normal text-[#4B5563]">
                                                    <span className="inline-flex select-none items-center">
                                                        {row.label}
                                                        {"tooltip" in row && row.tooltip ? (
                                                            <PriceSummaryInfoTooltip
                                                                label={row.tooltip.label}
                                                                text={row.tooltip.text}
                                                            />
                                                        ) : null}
                                                    </span>
                                                </td>
                                                <td className="pb-2 text-right text-[14px] leading-5 text-[#101828]">
                                                    {row.value}
                                                </td>
                                            </tr>
                                        ))}
                                        <tr>
                                            <td className="overflow-visible border-0 text-left text-[14px] leading-5 text-[#101828]">
                                                <span className="inline-flex select-none items-center">
                                                    {isUpcoming ? "Estimated On-Road Price" : "Total On-Road Price"}
                                                    {!isUpcoming ? (
                                                        <PriceSummaryInfoTooltip
                                                            label="Total on-road price and TCS"
                                                            text={CITY_PRICE_TOTAL_ON_ROAD_TOOLTIP_TEXT}
                                                        />
                                                    ) : null}
                                                </span>
                                            </td>
                                            <td className="text-right text-[15px] leading-5 font-bold text-[#008C8B]">
                                                {rtoResult || insuranceResult ? formatRupeeFull(totalOnRoadCity) : ""}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    {isDiscontinued ? (
                        <p className="p-3 text-[14px] border border-red-500 text-red-500 bg-red-50 rounded-lg text-left">
                            This {isVariantRoute ? "variant" : 'model'} has been discontinued.
                        </p>
                    ) : (
                        <>
                            {emiData && !isRtoLoading && !isInsuranceLoading && (
                                <EmiStartCard
                                    onClick={handleOpenEmiCalculator}
                                    emiAmount={emiData.amount}
                                    tooltipText={emiData.tooltip}
                                    TrackingEMI={EmiStartCardTracking}
                                    brand={model?.brand_name}
                                    model={model?.name}
                                />
                            )}
                            <LeadButton
                                {...(GORPFormHeading ?
                                    { formHeading: GORPFormHeading }
                                    :
                                    isPriceInCityRoute
                                        ? {
                                            formHeading: (
                                                <>
                                                    Get on road Price for{" "}
                                                    <span className="font-semibold text-[#008C8B]">
                                                        {model?.brand_name} {model?.name}
                                                    </span>{" "}
                                                    in{" "}
                                                    <span className="font-semibold text-[#008C8B]">
                                                        {selectedCity}
                                                    </span>
                                                </>
                                            )
                                        } : {})}
                                // btnText={
                                //     leadCTAText
                                //         ? leadCTAText
                                //         : model?.is_upcoming
                                //           ? 'Notify Me'
                                //           : isPriceInCityRoute
                                //             ? "Get Offers"
                                //             : `View ${OFFER_MONTH} Offers`
                                // }
                                btnText={
                                    leadCTAText
                                        ? leadCTAText
                                        : isUpcoming
                                          ? 'Notify Me'
                                          : isPriceInCityRoute
                                            ? "View Diwali Offers"
                                            : `View Diwali Offers`
                                }
                                carId={model?._id}
                                variantId={selectedVariant?._id || model?.variant_id}
                                carName={isPriceInCityRoute ? title : model?.name}
                                title={[
                                    model?.brand_name,
                                    model?.name,
                                    isVariantRoute ? (selectedVariant?.name || currentVariant?.name) : null,
                                ].filter(Boolean).join(" ") + " Offers"}
                                useSplashPopup={useSplashPopup || isPriceInCityRoute}
                                className={`lead-gradient w-full py-3 rounded-lg text-white font-semibold text-center cursor-pointer ${gorpTrackingClass}`}
                                is_upcoming={isUpcoming}
                                datatestid="car-lead-cta-button"
                            />
                        </>
                    )}
                </div>
            </div>
            {isEmiOpen && (
                <EmiCalculator
                    carName={`${model?.brand_name} ${model?.name}`}
                    variants={variants}
                    onRoadPrices={Object.keys(emiOnRoadPrices).length > 0 || (selectedVariant?._id && rtoResult && insuranceResult)
                        ? {
                            ...emiOnRoadPrices,
                            ...(selectedVariant?._id && rtoResult && insuranceResult
                                ? {
                                    [selectedVariant._id]: (selectedVariant?.price || price) + rtoResult.total + insuranceResult.total
                                }
                                : {})
                        }
                        : undefined}
                    cityName={selectedCity}
                    isEmiRoute={false}
                    isMobile={false}
                    selectedVariantId={emiSelectedVariant?._id}
                    onVariantChange={(nextVariant) => setEmiSelectedVariant(nextVariant)}
                    isOpen={isEmiOpen}
                    onClose={() => setIsEmiOpen(false)}
                    VarentSectorClass={PDP_EMICALCULATOR_POPUP_TRACKING.PDP_VARIANT_SELECTOR_CLICK}
                    DownPaymentClass={PDP_EMICALCULATOR_POPUP_TRACKING.PDP_DOWN_PAYMENT_CLICK}
                    LoanTermClass={PDP_EMICALCULATOR_POPUP_TRACKING.PDP_LOAN_PERIOD_CLICK}
                    InterestRateClass={PDP_EMICALCULATOR_POPUP_TRACKING.PDP_INTEREST_RATE_CLICK}
                />
            )}
        </>
    );
};

export default ProductHeroSection;
